import React, { ChangeEvent, SyntheticEvent, useEffect, useState } from 'react';
import { Autocomplete, Avatar, Box, CircularProgress, FormControl, InputLabel, MenuItem, Select, Stack, TextField, Typography } from '@mui/material';
import { Employee } from '../../../types/Employee';
import { UNIFORM_REQUEST_TYPES, UniformRequest, UniformRequestType } from '../../../types/UniformRequest';
import { collection, doc, getDoc, getDocs, getFirestore, limit, orderBy, query, where } from 'firebase/firestore';
import employeeConverter from '../../../converters/employeeConverter';
import { useDebouncedCallback } from 'use-debounce';

type Props = {
  employee?: Employee;
  uniformRequest?: UniformRequest;
  onChanged?: (e: Employee, t: UniformRequestType) => void;
};
export const UnifromRequestForm = (props: Props) => {
  const { uniformRequest, onChanged } = props;
  const eProp = props.employee;
  const editMode = uniformRequest !== undefined;

  const db = getFirestore();
  
  const [employee, setEmployee] = useState<Employee | undefined>(eProp);
  const [type, setType] = useState<UniformRequestType>('Complete Uniform');

  const [employees, setEmployees] = useState<Employee[]>([]);
  const [openAutocomplete, setOpenAutocomplete] = useState(false);
  const [loadingEmployees, setLoadingEmployees] = useState(false);

  const searchDebounced = useDebouncedCallback((value: string) => {
    setLoadingEmployees(true);
    setEmployees([]);
    getDocs(
      query(collection(db, 'Employees'), 
      where('idNumber', '>=', value), 
      where('idNumber', '<=', value + '\uf8ff'),
      where('status', '==', 'Active'),
      limit(15),
      orderBy('idNumber'))
    .withConverter(employeeConverter)).then((qSnapshot) => {
      setEmployees(qSnapshot.docs.map((d) => d.data()));
      setLoadingEmployees(false);
    });
  }, 1000);

  const handleOpenAutocomplete = () => setOpenAutocomplete(true);
  const handleCloseAutocomplete = () => setOpenAutocomplete(false);

  const handleTexInputOnChanged = (event: ChangeEvent<HTMLInputElement>) => {
    searchDebounced(event.currentTarget.value);
  };

  const handleAutocompleteSelectChange = (_e: SyntheticEvent<Element, Event>, value: Employee | null) => {
    setEmployee(value ?? undefined);
    if (value) {
      onChanged && onChanged(value, type);
    }
  };

  useEffect(() => {
    if (editMode) {
      // Get employee information
      const { idNumber, type } = uniformRequest;
      setType(type);
      if (eProp === undefined) {
        getDoc(doc(db, `Employees/${idNumber}`).withConverter(employeeConverter)).then(docSnapshot => {
          setEmployee(docSnapshot.data());
        });
      }
    } else {
      if (eProp) {
        setEmployee(employee);
      }
    }
  }, [editMode]);

  return (

    <Stack spacing={2.5}>
      <Box>
        {employee !== undefined ? (
          <Box mb={2}>
            <Stack direction='row' spacing={2}>
              <Avatar src={employee.picture} />
              <Stack>
                <Typography fontWeight='700'>{employee.name}</Typography>
                <Typography variant='caption'>{employee.idNumber}</Typography>
              </Stack>
            </Stack>
          </Box>
        ) : (
          <Box>
              <Autocomplete
                id="asynchronous-demo"
                fullWidth
                open={openAutocomplete}
                onOpen={handleOpenAutocomplete}
                onClose={handleCloseAutocomplete}
                isOptionEqualToValue={(option, value) => option.idNumber === value.idNumber}
                getOptionLabel={option => `${option.idNumber} - ${option.name}`}
                options={employees}
                loading={loadingEmployees}
                noOptionsText="Enter employee id number..."
                onChange={handleAutocompleteSelectChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Id Number"
                    onChange={handleTexInputOnChanged}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <React.Fragment>
                          {loadingEmployees ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                    }}
                  />
                )}
              />
          </Box>
        )}
      </Box>
      {employee && (
        <FormControl fullWidth>
          <InputLabel id="uniform-request-type-label">Type</InputLabel>
          <Select
            labelId="uniform-request-type-label"
            id="uniform-request-type-select"
            value={type}
            label="Type"
            onChange={(evt) => {
              setType(evt.target.value as UniformRequestType);
              onChanged && onChanged(employee, evt.target.value as UniformRequestType);
            }}
          >
            {UNIFORM_REQUEST_TYPES.map(v => {
              return (
                <MenuItem value={v}>{v}</MenuItem>
              );
            })}
          </Select>
        </FormControl>
      )}
    </Stack>
  );
};